<template>
  <Modal
    ref="modal"
    offset-menu
    :title="modalTitle"
    @close="cancel"
  >
    <div
      v-if="$features.has(source.viewFeatureFlag)"
      class="column"
    >
      <template v-if="!$features.hasCredits(source.featureAllowanceFlag)">
        <div v-if="isPrint">
          {{
            $t(`modals.view_mention_alert_modal.view_mention_text`, {
              platformName: appConfig.platformName,
              sourceFieldLabel: sourceFieldLabel,
            })
          }}

          <div class="q-mt-md">
            {{ $t("modals.view_mention_alert_modal.used_text") }}
            <i18n-t
              class="bold color-error"
              keypath="modals.view_mention_alert_modal.used_items_text"
              scope="global"
              tag="span"
            >
              <template #usedItems>
                {{ $features.getUsedCredits(source.featureAllowanceFlag) }}
              </template>
            </i18n-t>
          </div>
        </div>
        <div v-else>
          {{
            $t(`modals.view_mention_alert_modal.allowance_text`, {
              sourceFieldLabel: sourceFieldLabel,
              legalName: appConfig.legalName,
              organisationName: organisation.name,
              cost: toCurrency($features.getCost(source.featureAllowanceFlag)),
            })
          }}
        </div>
      </template>
      <template
        v-else-if="
          !source.viewWarningFeatureFlag || $features.reachedWarning(source)
        "
      >
        <div>
          <template v-if="isPrint">
            {{
              $t(`modals.view_mention_alert_modal.monthly_cap_text`, {
                platformName: appConfig.platformName,
                sourceFieldLabel: sourceFieldLabel,
              })
            }}
          </template>
          <template v-else>
            {{
              $t(`modals.view_mention_alert_modal.allowance_text`, {
                sourceFieldLabel: sourceFieldLabel,
                legalName: appConfig.legalName,
                organisationName: organisation.name,
                cost: toCurrency(
                  $features.getCost(source.featureAllowanceFlag)
                ),
              })
            }}
          </template>
          <div class="q-mt-md">
            {{ $t("modals.view_mention_alert_modal.used_text") }}
            <i18n-t
              class="bold"
              keypath="modals.view_mention_alert_modal.used_credits_text"
              scope="global"
              tag="span"
            >
              <template #usedCredits>
                <span
                  :class="{
                    'color-error': $features.noMoreMonthlyCredits(
                      source.featureAllowanceFlag
                    ),
                  }"
                >
                  {{ $features.getUsedCredits(source.featureAllowanceFlag) }}
                </span>
              </template>
              <template #credits>
                {{ getCredits(source.featureAllowanceFlag) }}
              </template>
            </i18n-t>
          </div>
        </div>
      </template>

      <div class="q-mt-md soft smaller">
        {{ $t("modals.view_mention_alert_modal.increase_cap_text") }}
        <ContactPopup :organisation="organisation">
          <span class="link pointer">
            &nbsp;{{ $t("modals.view_mention_alert_modal.account_manager") }}.
          </span>
        </ContactPopup>
      </div>
    </div>

    <template
      v-if="source.viewPopupAllowOverspend"
      #buttons
    >
      <BaseButton
        :label="$t('global.cancel')"
        new-design
        @click="closeAndBack"
      />
      <BaseButton
        color="primary"
        :label="continueText"
        new-design
        @click="continueToItem({ close: false })"
      />
    </template>
  </Modal>
</template>

<script>
import { storeToRefs } from "pinia";

import { getLocaleText } from "shared/boot/i18n";
import { BaseButton } from "shared/components/base";
import Modal from "shared/components/core/modals/Modal.vue";
import ContactPopup from "shared/components/user/ContactPopup.vue";
import useActivities from "shared/composables/useActivities";
import toCurrency from "shared/helpers/currency";
import { useGlobalStore } from "shared/stores/global";
import { useUserStore } from "shared/stores/user";

export default {
  name: "ViewMentionAlertModal",
  components: {
    Modal,
    BaseButton,
    ContactPopup,
  },
  props: {
    mention: {
      type: Object,
      required: true,
    },
    continueText: {
      type: String,
      default: () => getLocaleText("global.view"),
    },
  },
  emits: ["cancel", "continue"],
  setup(props) {
    const globalStore = useGlobalStore();
    const { appConfig } = storeToRefs(globalStore);

    const { source, recordActivity } = useActivities(props);

    const userStore = useUserStore();
    const { organisation } = storeToRefs(userStore);

    return {
      appConfig,
      source,

      recordActivity,
      organisation,
    };
  },
  computed: {
    modalTitle() {
      return this.$t("modals.view_mention_alert_modal.title", {
        sourceFieldLabel: this.$t(`source_field_labels.${this.source.field}`),
      });
    },
    isPrint() {
      return ["print", "magazine"].includes(this.source.field);
    },
    sourceFieldLabel() {
      return this.$t(`source_field_labels.${this.source.field}`);
    },
  },
  methods: {
    toCurrency,
    getCredits(feature) {
      return this.$features.getCredits(feature);
    },
    cancel() {
      this.$emit("cancel");
    },
    closeAndBack() {
      this.cancel();
      this.close();
    },
    continueToItem({ close = true } = {}) {
      this.recordActivity();

      this.$emit("continue");
      if (close) this.close();
    },
    close() {
      this.$refs.modal.close();
    },
  },
};
</script>
